import { distance, type Coordinate, type Network, type Pipe } from './network';
import { densityClass, LOOPS, NO_VALUE, type DensityLegendSettings } from './densityLegend';

export interface DensityDemand { coordinates: Coordinate; heat: number }

/** `density: null` = Sans valeur (distinct from 0, which is a real value of the first class). */
export interface PipeDensity { heat: number; length: number; density: number | null; loop: boolean }

export interface DensityResult {
  signature: string;
  value: number;
  byPipe: Record<string, PipeDensity>;
  /** Set once the calculation data changed: kept for display, never presented as up to date. */
  stale?: boolean;
}
export type PipeDensityState = { kind: 'loop' } | { kind: 'none' } | { kind: 'value'; density: number };

export const pipeLength = (p: Pipe) => p.coordinates.slice(1)
  .reduce((sum, c, i) => sum + distance(p.coordinates[i], c), 0);

/** Loops take priority over density; a pipe without result is Sans valeur. */
export function pipeDensityState(entry: PipeDensity | undefined): PipeDensityState {
  if (entry?.loop) return { kind: 'loop' };
  return entry && entry.density !== null ? { kind: 'value', density: entry.density } : { kind: 'none' };
}

export function pipeDensityColor(entry: PipeDensity | undefined, legend: DensityLegendSettings): string {
  const state = pipeDensityState(entry);
  return state.kind === 'loop' ? LOOPS.color : state.kind === 'none' ? NO_VALUE.color
    : legend.colors[densityClass(state.density, legend.thresholds)];
}

/** Vertices closer than this are the same network node (hand-drawn pipes are never exact). */
export const LOOP_TOLERANCE_M = 10;

/**
 * A pipe belongs to a loop when its own path revisits a node, or when two of its nodes
 * stay connected through the other pipes once it is removed.
 */
export function loopPipeIds(pipes: Pipe[], tolerance = LOOP_TOLERANCE_M): Set<string> {
  const nodes: Coordinate[] = [];
  const nodeOf = (c: Coordinate) => {
    const found = nodes.findIndex((n) => distance(n, c) <= tolerance);
    return found >= 0 ? found : nodes.push(c) - 1;
  };
  const paths = pipes.map((p) => p.coordinates.map(nodeOf).filter((n, i, all) => i === 0 || n !== all[i - 1]));
  const loops = new Set<string>();
  pipes.forEach((pipe, index) => {
    const path = paths[index];
    if (new Set(path).size < path.length) { loops.add(pipe.id); return; }
    const parent = nodes.map((_, i) => i);
    const root = (n: number): number => (parent[n] === n ? n : (parent[n] = root(parent[n])));
    paths.forEach((other, j) => {
      if (j !== index) other.slice(1).forEach((n, k) => { parent[root(other[k])] = root(n); });
    });
    if (new Set(path.map(root)).size < path.length) loops.add(pipe.id);
  });
  return loops;
}
export const densitySignature = (network: Network, buildings: string, heat: number) =>
  JSON.stringify({ plants: network.plants, pipes: network.pipes, buildings, heat });

/** Local planar distance for assignment only; lengths remain geodesic. */
function distanceToPipe(point: Coordinate, pipe: Pipe): number {
  const x = Math.cos(point[1] * Math.PI / 180);
  return Math.min(...pipe.coordinates.slice(1).map((b, i) => {
    const a = pipe.coordinates[i];
    const dx = (b[0] - a[0]) * x, dy = b[1] - a[1];
    const px = (point[0] - a[0]) * x, py = point[1] - a[1];
    const t = Math.max(0, Math.min(1, (px * dx + py * dy) / (dx * dx + dy * dy || 1)));
    return Math.hypot(px - t * dx, py - t * dy);
  }));
}

/** Nearest-pipe demand allocation, NOT thermal flow or the product formula. */
export function simulateDensity(network: Network, heat: number, signature: string, demands?: DensityDemand[]): DensityResult {
  if (!network.plants.length || !network.pipes.length) throw new Error('Calcul indisponible.');
  const total = network.pipes.reduce((sum, p) => sum + pipeLength(p), 0);
  if (!(total > 0) || !Number.isFinite(heat) || heat < 0) throw new Error('Géométrie ou chaleur invalide.');
  const value = heat / total;
  const loops = loopPipeIds(network.pipes);
  const byPipe: Record<string, PipeDensity> = Object.fromEntries(network.pipes.map((p) => {
    const length = pipeLength(p);
    return [p.id, { length, heat: 0, density: null, loop: loops.has(p.id) }];
  }));
  // Loop pipes cannot carry a density: demand goes to the nearest pipe outside loops.
  const eligible = network.pipes.filter((p) => byPipe[p.id].length > 0 && !byPipe[p.id].loop);
  const loads = demands ?? [{ coordinates: network.pipes[0].coordinates[0], heat }];
  for (const demand of eligible.length ? loads : []) {
    const pipe = [...eligible].sort((a, b) => {
      const delta = distanceToPipe(demand.coordinates, a) - distanceToPipe(demand.coordinates, b);
      return Math.abs(delta) > 1e-10 ? delta
        : Number(a.kind !== 'manual') - Number(b.kind !== 'manual') || a.id.localeCompare(b.id);
    })[0];
    byPipe[pipe.id].heat += demand.heat;
  }
  for (const item of Object.values(byPipe)) {
    item.density = item.loop || !(item.length > 0) ? null : item.heat / item.length;
  }
  return { signature, value, byPipe };
}
